/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Social Login OAuth2 Buttons (Google, GitHub, Apple) (Task 7.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useEffect, useState } from 'react';
import { useI18nStore } from '../../stores/useI18nStore';
import { getApiBaseUrl } from '../../lib/api-client';

export interface SocialLoginButtonsProps {
  redirectPath?: string;
  className?: string;
  onProviderClick?: (provider: 'google' | 'github' | 'apple') => void;
}

export interface EnabledProviders {
  google: boolean;
  github: boolean;
  apple: boolean;
}

export const SocialLoginButtons: React.FC<SocialLoginButtonsProps> = ({
  redirectPath = '/workspace/dashboard',
  className = '',
  onProviderClick,
}) => {
  const { t } = useI18nStore();
  const [providers, setProviders] = useState<EnabledProviders>({
    google: false,
    github: false,
    apple: false,
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let mounted = true;

    const fetchProviders = async () => {
      try {
        const apiUrl = `${getApiBaseUrl()}/api/v1/auth/oauth/providers`;
        const res = await fetch(apiUrl);
        if (res.ok) {
          const json = (await res.json()) as { success: boolean; data: EnabledProviders };
          if (mounted && json.success && json.data) {
            setProviders(json.data);
          }
        }
      } catch (err) {
        console.warn('[SocialLoginButtons] Failed to fetch enabled providers:', err);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    void fetchProviders();
    return () => {
      mounted = false;
    };
  }, []);

  const handleOAuthClick = (provider: 'google' | 'github' | 'apple') => {
    if (onProviderClick) {
      onProviderClick(provider);
      return;
    }

    const baseUrl = getApiBaseUrl();
    const targetUrl = `${baseUrl}/api/v1/auth/oauth/${provider}?redirect=${encodeURIComponent(redirectPath)}`;
    window.location.href = targetUrl;
  };

  // If loading or none of the social providers are enabled, don't show buttons
  const hasAnyProvider = providers.google || providers.github || providers.apple;
  if (!isLoading && !hasAnyProvider) {
    return null;
  }

  return (
    <div className={`space-y-2.5 w-full ${className}`}>
      {/* Google Sign-In Button */}
      {providers.google && (
        <button
          type="button"
          onClick={() => handleOAuthClick('google')}
          className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-xl border border-surface-border bg-surface hover:bg-muted/30 text-foreground font-medium text-xs sm:text-sm transition-all shadow-sm hover:border-surface-border/80 focus:outline-none focus:ring-2 focus:ring-primary/20 active:scale-[0.99]"
          aria-label="Continue with Google"
        >
          <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.66-5.17 3.66-9.17z"
            />
            <path
              fill="#34A853"
              d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z"
            />
            <path
              fill="#FBBC05"
              d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.25C.45 8.18 0 9.99 0 12s.45 3.82 1.25 5.42l4.03-3.15z"
            />
            <path
              fill="#EA4335"
              d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z"
            />
          </svg>
          <span>{t('auth.continueWithGoogle') || 'Continue with Google'}</span>
        </button>
      )}

      {/* GitHub Sign-In Button */}
      {providers.github && (
        <button
          type="button"
          onClick={() => handleOAuthClick('github')}
          className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-xl border border-surface-border bg-surface hover:bg-muted/30 text-foreground font-medium text-xs sm:text-sm transition-all shadow-sm hover:border-surface-border/80 focus:outline-none focus:ring-2 focus:ring-primary/20 active:scale-[0.99]"
          aria-label="Continue with GitHub"
        >
          <svg className="w-4 h-4 shrink-0 fill-current text-foreground" viewBox="0 0 24 24">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.53 1.032 1.53 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
            />
          </svg>
          <span>{t('auth.continueWithGithub') || 'Continue with GitHub'}</span>
        </button>
      )}

      {/* Apple Sign-In Button */}
      {providers.apple && (
        <button
          type="button"
          onClick={() => handleOAuthClick('apple')}
          className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-xl border border-surface-border bg-surface hover:bg-muted/30 text-foreground font-medium text-xs sm:text-sm transition-all shadow-sm hover:border-surface-border/80 focus:outline-none focus:ring-2 focus:ring-primary/20 active:scale-[0.99]"
          aria-label="Continue with Apple"
        >
          <svg className="w-4 h-4 shrink-0 fill-current text-foreground" viewBox="0 0 24 24">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.37c.62-.77 1.04-1.85.93-2.92-.9.04-1.99.6-2.63 1.37-.56.65-.95 1.75-.82 2.8 1 .08 2-.51 2.52-1.25z" />
          </svg>
          <span>{t('auth.continueWithApple') || 'Continue with Apple'}</span>
        </button>
      )}
    </div>
  );
};

